<main>
  @if (isLoading() > 0) {
    <loading-overlay [isLoading]="true"></loading-overlay>
  }

  <header class="page-header">
    <div class="headline">
      <h1>{{ title() }}</h1>
      @if (!isNewRecord() && batch()) {
        <csl-dropdown [formControl]="status" [enumObject]="statusEnumObject" [enumValueOptions]="availableStatuses()" [disabled]="!canChangeStatus()" />
      }
    </div>
    <div class="buttons">
      @if (isNewRecord() && canEdit()) {
        <p-button [label]="'batchDetails.createBatch' | translateWF: 'Create Batch'" icon="pi pi-save" (onClick)="save()" [loading]="isSaving()" />
      }
      <p-button [label]="'Cancel' | translateWF: 'Cancel'" variant="outlined" (onClick)="cancel()" />
    </div>
  </header>

  @if (isNewRecord()) {
    <div class="details-content">
      @if (sourcePwo(); as pwo) {
        <p-panel [header]="'batchDetails.productionWorkOrder' | translateWF: 'Production Work Order'">
          <p-card>
            <div class="flex items-center gap-3 mt-2">
              <span class="text-sm text-gray-500">{{ 'batchDetails.pwo' | translateWF: 'PWO' }}:</span>
              <span class="font-medium">{{ pwo.number }}</span>
            </div>
          </p-card>
        </p-panel>

        <p-panel>
          <ng-template pTemplate="header">
            <div class="flex flex-col">
              <span>{{ 'batchDetails.selectRecipe' | translateWF: 'Select Recipe' }}</span>
              <p class="text-sm text-gray-500">
                {{ 'batchDetails.selectRecipeHint' | translateWF: 'Choose a recipe from the Production Work Order items to create a batch.' }}
              </p>
            </div>
          </ng-template>
          <p-card>
            <div class="recipe-selection-grid">
              @for (item of availableRecipes(); track item.id) {
                <div class="recipe-card" [class.selected]="selectedRecipeItem()?.id === item.id" (click)="onRecipeItemSelect(item)">
                  <div class="recipe-card-header">
                    <span class="font-medium">{{ item.productRecipeNumber }}</span>
                    @if (item.productRecipe?.name; as recipeName) {
                      <span class="text-gray-500 text-sm">{{ recipeName }}</span>
                    }
                  </div>
                  <div class="recipe-card-meta">
                    <span>{{ 'Quantity' | translateWF: 'Quantity' }}: {{ item.quantity }} kg</span>
                  </div>
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        @if (selectedRecipeItem()) {
          <p-panel [header]="'batchDetails.title' | translateWF: 'Batch Details'">
            <p-card>
              <div class="grid-2">
                <div class="form-box">
                  <label>{{ 'batchDetails.productRecipe' | translateWF: 'Product Recipe' }}</label>
                  <input pInputText [value]="createForm.controls.productDevelopmentNumber.value ?? ''" disabled class="w-full" />
                </div>
                <div class="form-box">
                  <label>{{ 'batchDetails.plannedQuantity' | translateWF: 'Planned Quantity (kg)' }}</label>
                  <p-inputNumber
                    [formControl]="createForm.controls.plannedQuantity"
                    [min]="0.01"
                    mode="decimal"
                    [minFractionDigits]="2"
                    suffix=" kg"
                    styleClass="w-full"
                    placeholder="0.00"
                  />
                </div>
              </div>
            </p-card>
          </p-panel>
        }
      } @else {
        <div class="details-content">
          <div class="empty-state">
            <i class="pi pi-inbox text-3xl text-gray-300 mb-2"></i>
            <p class="text-gray-400 text-sm">
              {{ 'batchDetails.noPwoHint' | translateWF: 'Navigate from a Production Work Order to create a batch.' }}
            </p>
            <p-button [label]="'batchDetails.goToPwos' | translateWF: 'Go to Production Work Orders'" variant="outlined" (onClick)="goToProductionWorkOrders()" class="mt-3" />
          </div>
        </div>
      }
    </div>
  }

  @if (!isNewRecord() && batch(); as currentBatch) {
    <div class="content-grid">
      <div class="col-left">
        <div class="metric-grid">
          <div class="metric-card">
            <div class="label">{{ 'batchDetails.batch' | translateWF: 'Batch' }}</div>
            <div class="value">{{ currentBatch.number }}</div>
          </div>
        </div>

        <div class="item-code-card">
          <div class="label">{{ 'batchDetails.productRecipe' | translateWF: 'Product Recipe' }}</div>
          <div>
            <a class="item-code-link">{{ currentBatch.productDevelopmentNumber }}</a>
            @if (productRecipe(); as recipe) {
              <span class="text-gray-500 ml-2">— {{ recipe.name }}</span>
            }
          </div>
        </div>

        @if (productionWorkOrder(); as pwo) {
          <p-panel [header]="'batchDetails.productionWorkOrder' | translateWF: 'Production Work Order'">
            <ng-template pTemplate="icons">
              <a class="wo-number cursor-pointer text-primary hover:underline" (click)="goToProductionWorkOrder()">{{ pwo.number }}</a>
            </ng-template>

            @if (clientPOAllocations().length > 0) {
              <p-card data-variant="compact">
                <p class="section-description">
                  {{ 'batchDetails.clientPoReservations' | translateWF: 'Client PO reservations for this recipe:' }}
                </p>
                <p-table [value]="clientPOAllocations()" styleClass="p-datatable-sm">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ 'batchDetails.clientPo' | translateWF: 'Client PO' }}</th>
                      <th>{{ 'Recipe' | translateWF: 'Recipe' }}</th>
                      <th class="text-right">{{ 'batchDetails.reservedQty' | translateWF: 'Reserved Qty' }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-alloc>
                    <tr>
                      <td class="font-medium">{{ alloc.clientPurchaseOrderNumber }}</td>
                      <td>{{ alloc.clientPurchaseOrderProductRecipeNumber }}</td>
                      <td class="text-right">{{ alloc.reservedQuantity }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              </p-card>
            }
          </p-panel>
        }

        <p-panel [header]="'batchDetails.batchSummary' | translateWF: 'Batch Summary'">
          <p-card>
            <div class="summary-metrics">
              <div>
                <div class="label">{{ 'Planned' | translateWF: 'Planned' }}</div>
                <div class="metric-value">{{ currentBatch.plannedQuantity }} kg</div>
              </div>
            </div>

            <p-table [value]="consumptionSummary()" styleClass="p-datatable-sm mt-3">
              <ng-template pTemplate="header">
                <tr>
                  <th></th>
                  <th class="text-right">{{ 'Estimated' | translateWF: 'Estimated' }}</th>
                  <th class="text-right">{{ 'Actual' | translateWF: 'Actual' }}</th>
                  <th>{{ 'Difference' | translateWF: 'Difference' }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td class="font-medium">{{ row.labelKey | translateWF: row.fallback }}</td>
                  <td class="text-right text-gray-500">
                    @if (row.estimated !== null) {
                      {{ row.estimated | number: '1.2-2' }} {{ row.unit }}
                    } @else {
                      —
                    }
                  </td>
                  <td class="text-right">{{ row.actual | number: '1.2-2' }} {{ row.unit }}</td>
                  <td><app-consumption-variance [variance]="row.variance" [unit]="row.unit" /></td>
                </tr>
              </ng-template>
            </p-table>

            <div class="mt-4 flex flex-col gap-3">
              <div>
                <label for="manufacturerLotNumber" class="label mb-1 block required">{{ 'Batch.manufacturer_lot_number' | translateWF: 'Manufacturer Lot Number' }}</label>
                <input
                  id="manufacturerLotNumber"
                  pInputText
                  type="text"
                  class="w-full"
                  [formControl]="manufacturerLotNumber"
                  [placeholder]="'Batch.manufacturer_lot_number' | translateWF: 'Manufacturer Lot Number'"
                />
              </div>
              <div>
                <div class="label mb-1">{{ 'Batch.expiry_date' | translateWF: 'Expiry Date' }}</div>
                <p-datepicker
                  class="w-full"
                  [formControl]="expiryDate"
                  [iconDisplay]="'input'"
                  [showIcon]="true"
                  dateFormat="yy-mm-dd"
                  [placeholder]="'Batch.expiry_date' | translateWF: 'Expiry Date'"
                />
              </div>
              <div class="flex items-center gap-2">
                <p-button [label]="'Save' | translateWF: 'Save'" icon="pi pi-check" size="small" [disabled]="!canEditBatchFields()" (onClick)="saveBatchFields()" />
                @if (!canEditBatchFields() && (lotItems().length > 0 || currentBatch.isManufacturerLotNumberLocked || currentBatch.isExpiryDateLocked)) {
                  <i class="pi pi-lock text-gray-400"></i>
                  <small class="text-gray-500">
                    {{ 'Batch.fields_locked' | translateWF: 'Locked — a lot item has already been created for this batch.' }}
                  </small>
                }
              </div>
            </div>
          </p-card>
        </p-panel>
      </div>

      <div class="col-right">
        <p-panel [header]="'Stations' | translateWF: 'Stations'">
          <div class="flex flex-col gap-[1rem]">
            <p-panel data-tone="surface" [toggleable]="true" [collapsed]="true" styleClass="finishing-routes-card">
              <ng-template pTemplate="header">
                <div class="card-header-custom">
                  <h3>{{ 'batchDetails.requisitions' | translateWF: 'Requisitions' }}</h3>
                  <div class="flex items-center gap-2">
                    <span class="text-sm text-gray-500">{{ requisitions().length }}</span>
                    @if (canCreateRequisition()) {
                      <p-button [label]="'batchDetails.createRequisition' | translateWF: 'Create Requisition'" icon="pi pi-plus" size="small" (onClick)="goToCreateRequisition()" />
                    }
                  </div>
                </div>
              </ng-template>

              <p-card data-variant="compact">
                @if (requisitions().length === 0) {
                  <div class="text-center py-4 text-gray-400 text-sm">
                    <i class="pi pi-inbox text-2xl mb-2"></i>
                    <p>{{ 'batchDetails.noRequisitions' | translateWF: 'No requisitions' }}</p>
                  </div>
                } @else {
                  <div class="flex flex-col gap-3 p-3">
                    @for (row of requisitionRows(); track row.requisition.id) {
                      @let req = row.requisition;
                      <div class="requisition-item">
                        <div class="flex items-center justify-between mb-2">
                          <span class="font-medium text-sm">{{ req.requisitionNumber }}</span>
                          <p-tag [value]="row.status.label" [severity]="row.status.severity" />
                        </div>

                        @if (req.requisitionEntries.length > 0) {
                          <p-table [value]="req.requisitionEntries" styleClass="p-datatable-sm">
                            <ng-template pTemplate="header">
                              <tr>
                                <th>{{ 'batchDetails.itemCode' | translateWF: 'Item Code' }}</th>
                                <th>{{ 'batchDetails.materialFor' | translateWF: 'Material For' }}</th>
                                <th class="text-right">{{ 'Quantity' | translateWF: 'Quantity' }}</th>
                              </tr>
                            </ng-template>
                            <ng-template pTemplate="body" let-entry>
                              <tr>
                                <td class="font-medium">{{ entry.itemCode }}</td>
                                <td>{{ entry.materialForItemCode }}</td>
                                <td class="text-right">{{ entry.requiredQuantityInUnitOfMeasure }}</td>
                              </tr>
                            </ng-template>
                          </p-table>
                        }

                        @if (req.lotItems && req.lotItems.length > 0) {
                          <div class="mt-2">
                            <span class="text-xs font-medium text-gray-500 mb-1 block">
                              {{ 'batchDetails.lotItems' | translateWF: 'Lot Items' }}
                            </span>
                            <p-table [value]="req.lotItems" styleClass="p-datatable-sm">
                              <ng-template pTemplate="header">
                                <tr>
                                  <th>{{ 'batchDetails.lotNumber' | translateWF: 'Lot #' }}</th>
                                  <th>{{ 'Item' | translateWF: 'Item' }}</th>
                                  <th class="text-right">{{ 'batchDetails.netQty' | translateWF: 'Net Qty' }}</th>
                                  <th class="text-right">{{ 'batchDetails.grossQty' | translateWF: 'Gross Qty' }}</th>
                                  <th>{{ 'Location' | translateWF: 'Location' }}</th>
                                </tr>
                              </ng-template>
                              <ng-template pTemplate="body" let-lot>
                                <tr>
                                  <td class="font-medium">{{ lot.number }}</td>
                                  <td>
                                    {{ lot.itemCode }}
                                    @if (lot.item?.fieldToDisplay) {
                                      <span class="text-gray-500 text-xs ml-1">{{ lot.item.fieldToDisplay }}</span>
                                    }
                                  </td>
                                  <td class="text-right">{{ lot.netQuantity }}</td>
                                  <td class="text-right">{{ lot.grossQuantity }}</td>
                                  <td>{{ locationPathService.label(lot.currentLocationCode) }}</td>
                                </tr>
                              </ng-template>
                            </p-table>
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </p-card>
            </p-panel>

            <p-card styleClass="finishing-routes-card">
              <ng-template pTemplate="header">
                <div class="card-header-custom">
                  <h3>{{ 'batchDetails.dyeingCycleRuns' | translateWF: 'Dyeing Cycle Runs' }}</h3>
                  <div class="flex items-center gap-2">
                    @if (dyeingCycles().length > 0) {
                      <p-tag [value]="dyeingCyclesStatusTag().label" [severity]="dyeingCyclesStatusTag().severity" />
                    }
                    @if (canStartDyeingCycle()) {
                      <p-button [label]="'batchDyeingCycle.startCycle' | translateWF: 'Start Cycle'" icon="pi pi-play" size="small" (onClick)="openDyeingCycleModal()" />
                    }
                  </div>
                </div>
              </ng-template>

              @if (dyeingCycles().length === 0) {
                <div class="text-center py-4 text-gray-400 text-sm">
                  <i class="pi pi-inbox text-2xl mb-2"></i>
                  <p>{{ 'batchDetails.noDyeingCycles' | translateWF: 'No dyeing cycles' }}</p>
                </div>
              } @else {
                <div class="dyeing-cycles-list">
                  @for (row of dyeingCycleRows(); track row.cycle.id) {
                    @let cycle = row.cycle;
                    <div class="cycle-item">
                      <div class="cycle-header">
                        <span class="cycle-name">
                          {{ cycle.startStationAssetId ?? ('N_A' | translateWF: 'N/A') }} — {{ 'batchDyeingCycle.cycle' | translateWF: 'Cycle' }} #{{ cycle.cycleNumber }}
                        </span>
                        @if (!row.isCompleted && canEdit()) {
                          <p-button
                            icon="pi pi-cog"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            [pTooltip]="'batchDetails.manageCycle' | translateWF: 'Manage Cycle'"
                            tooltipPosition="top"
                            (onClick)="openDyeingCycleModal(cycle)"
                          />
                        } @else if (row.isCompleted) {
                          <p-button
                            icon="pi pi-eye"
                            [text]="true"
                            [rounded]="true"
                            size="small"
                            [pTooltip]="'batchDetails.viewCycle' | translateWF: 'View Cycle'"
                            tooltipPosition="top"
                            (onClick)="openDyeingCycleModal(cycle)"
                          />
                        }
                      </div>
                      <div class="cycle-dates">
                        [{{ cycle.startTime | date: 'dd/MM/yy HH:mm' }}] — [{{
                          cycle.endTime ? (cycle.endTime | date: 'dd/MM/yy HH:mm') : ('In_Progress' | translateWF: 'In Progress')
                        }}]
                      </div>
                      <div class="flex items-center gap-2 mt-1">
                        <p-tag [value]="row.approval.label" [severity]="row.approval.severity" />
                      </div>
                      @if (cycle.colorStatusApproval && !cycle.colorStatusApproval.isApproved && cycle.colorStatusApproval.comments) {
                        <div class="cycle-rejection-note mt-1">{{ cycle.colorStatusApproval.comments }}</div>
                      }
                    </div>
                  }
                </div>
              }
            </p-card>

            <p-card data-variant="compact" styleClass="finishing-routes-card">
              <ng-template pTemplate="header">
                <div class="card-header-custom">
                  <h3>{{ 'batchDetails.containers' | translateWF: 'Containers (Lot Items)' }}</h3>
                  <div class="flex items-center gap-2">
                    @if (lotItems().length > 0) {
                      <span class="text-sm text-gray-500">{{ lotItems().length }}</span>
                    }
                    @if (canEdit()) {
                      <p-button [label]="'Create' | translateWF: 'Create'" icon="pi pi-plus" size="small" (onClick)="openCreateLotItemModal()" />
                    }
                  </div>
                </div>
              </ng-template>

              @if (lotItems().length === 0) {
                <div class="text-center py-4 text-gray-400 text-sm">
                  <i class="pi pi-inbox text-2xl mb-2"></i>
                  <p>{{ 'batchDetails.noLotItems' | translateWF: 'No lot items' }}</p>
                </div>
              } @else {
                <p-table [value]="lotItems()" styleClass="p-datatable-sm">
                  <ng-template pTemplate="header">
                    <tr>
                      <th>{{ 'Number' | translateWF: 'Number' }}</th>
                      <th>{{ 'batchDetails.itemCode' | translateWF: 'Item Code' }}</th>
                      <th class="text-right">{{ 'batchDetails.netQty' | translateWF: 'Net Qty' }}</th>
                      <th class="text-right">{{ 'batchDetails.grossQty' | translateWF: 'Gross Qty' }}</th>
                      <th>{{ 'Location' | translateWF: 'Location' }}</th>
                      <th>{{ 'Batch.manufacturer_lot_number' | translateWF: 'Manufacturer Lot Number' }}</th>
                      <th>{{ 'Batch.expiry_date' | translateWF: 'Expiry Date' }}</th>
                    </tr>
                  </ng-template>
                  <ng-template pTemplate="body" let-item>
                    <tr>
                      <td class="font-medium">{{ item.number }}</td>
                      <td>{{ item.itemCode }}</td>
                      <td class="text-right">{{ item.netQuantity }}</td>
                      <td class="text-right">{{ item.grossQuantity }}</td>
                      <td>{{ locationPathService.label(item.currentLocationCode) }}</td>
                      <td>{{ item.providerLotId }}</td>
                      <td>{{ item.expiryDate ? (item.expiryDate | date: 'yyyy-MM-dd') : '—' }}</td>
                    </tr>
                  </ng-template>
                </p-table>
              }
            </p-card>
          </div>
        </p-panel>
      </div>
    </div>
  }
</main>
